import createCachedSelector from 're-reselect';
import { DEFAULT_PARTNER, getOfferPartner } from 'features/common/selectors';
import { emptyArray } from 'constants/emptyConst';
import { partnerType } from 'constants/partnerType';

export const initialPartnerData = {
  partnerId: null,
  isLoaded: false,
  loading: false,
  expanded: true,
  loadedByUniqueId: null
};

export const stringifyCacheKey = props => {
  return JSON.stringify({ ...props });
};

export const getOfferClientPartnerTypeCode = state => {
  const client = state.currentOffer.header.client;

  if (client) return client.partnerTypeCode;

  return partnerType.PF;
};

export const getOfferType = state => {
  return state.currentOffer.header.offerTypeId;
};
export const getDocumentType = state => {
  return state.currentOffer.header.documentTypeId;
};

export const getExternalVerificationStatus = (state, ownProps) => {
  let partnerUniqueId = ownProps?.partnerGeneralData?.uniqueId;
  return (
    !!state.clientData?.clients?.byUniqueId[partnerUniqueId]?.isExternalVerificationInProgress ||
    !!state.clientData?.clients?.fromPartnerQuery[partnerUniqueId]?.isExternalVerificationInProgress
  );
};

export const getExternalVerificationStatusByUniqueId = (state, uniqueId, partnerId) => {
  if (!uniqueId) uniqueId = state.currentOffer?.clientData?.partners[partnerId]?.loadedByUniqueId;
  return (
    !!state.clientData?.clients?.byUniqueId[uniqueId]?.isExternalVerificationInProgress ||
    !!state.clientData?.clients?.fromPartnerQuery[uniqueId]?.isExternalVerificationInProgress
  );
};

export const getPartnerExternalVerificationStatus = (state, ownProps) => {
  let partnerUniqueId = ownProps?.partner?.generalData?.uniqueId;
  return (
    !!state.clientData?.clients?.byUniqueId[partnerUniqueId]?.isExternalVerificationInProgress ||
    !!state.clientData?.clients?.fromPartnerQuery[partnerUniqueId]?.isExternalVerificationInProgress
  );
};

export const getRefreshedOfferPartnerStatus = state => {
  return !!state.clientData?.clients?.fromPartnerQuery.status;
};

export const getCountiesData = (state, countryId) => state.clientData.counties.byCountryId[countryId];

export const getCountiesByCountryId = (state, countryId) => state.clientData.counties.byCountryId[countryId]?.data || emptyArray;

export const getCountiesLoadedByCountryId = createCachedSelector(getCountiesData, counties => {
  return counties?.loaded || false;
})((_, countryId) => countryId);

export const getCountiesLoadingByCountryId = createCachedSelector(getCountiesData, counties => {
  return counties?.loading || false;
})((_, countryId) => countryId);

export const getCurrencyCode = state => state.currentOffer.header.currencyCode;

export const getClientData = state => state.currentOffer.clientData;
export const getDefaultAddressData = state => state.clientData.defaultAddress;
const getPartnerData = (state, partnerId) => {
  return state.currentOffer.clientData.partners[partnerId] || initialPartnerData;
};
export const getClient = state => getPartnerData(state, state.currentOffer.clientData.client);
export const getAssociatedPartner = (state, partnerId) =>
  state.currentOffer.clientData.associatedPartners.some(ap => ap === partnerId) && getPartnerData(state, partnerId);

export const getPartnerIsLoadedByUniqueId = createCachedSelector(getPartnerData, partner => {
  return partner.loadedByUniqueId;
})((_, partnerId) => partnerId);

export const getPartnerIsLoaded = createCachedSelector(getPartnerData, partner => {
  return Boolean(partner.isLoaded);
})((_, partnerId) => partnerId);

export const getPartnerLoading = createCachedSelector(getPartnerData, partner => Boolean(partner.loading))((_, partnerId) => partnerId);

export const getPartnerExpanded = createCachedSelector(getPartnerData, partner => partner?.expanded ?? true)((_, partnerId) => partnerId);

export const getPartnersByUniqueId = state => state.clientData.clients.byUniqueId;

export const getPartnerFromQuery = (state, uniqueId) => {
  return state.clientData.clients.fromPartnerQuery[uniqueId];
};

export const getFullPartner = createCachedSelector(
  state => state,
  getPartnerIsLoaded,
  getPartnerData,
  getOfferPartner,
  (_state, isLoaded, partner, entitiesOfferPartner) => {
    if (partner.partnerId === null || !isLoaded) {
      return DEFAULT_PARTNER;
    }

    return entitiesOfferPartner;
  }
)((_state, partnerId) => partnerId);

export const getPartnerByUniqueId = createCachedSelector(
  state => state,
  getPartnerIsLoadedByUniqueId,
  getFullPartner,
  (state, loadedByUniqueId, partner) => {
    if (!loadedByUniqueId) {
      return partner;
    }

    return state.clientData.clients.byUniqueId[loadedByUniqueId] || DEFAULT_PARTNER;
  }
)((_state, partnerId) => partnerId);

const getAddresses = (state, partnerId) => state.entities.offerPartnerAddresses[partnerId]?.addresses || emptyArray;
const getPartnerAddressIds = (state, partnerId) => state.currentOffer.clientAddressesData[partnerId]?.ids ?? emptyArray;

export const getPartnerAddresses = createCachedSelector(getAddresses, getPartnerAddressIds, (addresses, ids) => {
  const result = addresses.filter(address => ids.includes(address.partnerAddressId));
  return result;
})((_state, partnerId) => partnerId);

export const getShouldRefreshAddresses = (state, partnerId) =>
  (state.currentOffer.clientAddressesData[partnerId]?.refreshAddresses ?? false) &&
  (state.currentOffer.clientAddressesData[partnerId]?.loaded ?? false);

export const getAddressTypesLoaded = state => {
  return state.dictionaries.addressTypes && state.dictionaries.addressTypes.isLoaded;
};

export const getAddressTypes = state =>
  state.dictionaries.addressTypes.items && state.dictionaries.addressTypes.isLoaded ? state.dictionaries.addressTypes.items : emptyArray;

export const getRealBeneficiaryRole = createCachedSelector(
  offerConfig => offerConfig.realBeneficiaryPartnerRoleId,
  offerConfig => offerConfig.realBeneficiaryPartnerRoleCode,
  offerConfig => offerConfig.realBeneficiaryPartnerRoleName,
  (partnerRoleId, partnerRoleCode, partnerRoleName) => ({
    partnerRoleId,
    partnerRoleCode,
    name: partnerRoleName
  })
)((_offerConfig, partnerId) => partnerId);
